import { iframeDataBridge } from '@/iframe/iframeDataBridge.js';
import { authenticatedFetch, getApiUrl } from '@/utils/authService.js';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';

const productTreeCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	logCategory: 'ProductTree',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.productTrees.`,
});

function transformProductTreeData(items) {
	if (!Array.isArray(items) || items.length === 0) return [];

	const transformed = items.map((item) => ({
		id: item.id,
		label: item.label,
		title: item.label,
		parentId: item.parentId,
		depth: item.depth,
		children: [],
	}));

	const itemMap = new Map();
	transformed.forEach((item) => itemMap.set(item.id, item));

	const roots = [];
	transformed.forEach((item) => {
		if (item.parentId === null || item.parentId === 0 || !itemMap.has(item.parentId)) {
			roots.push(item);
		} else {
			const parent = itemMap.get(item.parentId);
			if (parent) {
				parent.children.push(item);
			}
		}
	});

	return roots;
}

export async function getProductTrees(forceRefresh = false) {
	try {
		if (!forceRefresh) {
			const cached = await productTreeCache.get('all');
			if (cached) {
				log('info', 'ProductTree', 'Returning cached product trees');
				return cached;
			}

			try {
				const legacyData = await iframeDataBridge.getStorageAsync('PRODUCTTREE');
				if (legacyData) {
					log('info', 'ProductTree', '📦 Seeding Vue cache from legacy storage (PRODUCTTREE)');
					await productTreeCache.put('all', legacyData);
					return legacyData;
				}
			} catch (legacyError) {
				log('warn', 'ProductTree', `⚠️ Legacy storage check failed: ${legacyError.message}`);
			}
		}

		if (iframeDataBridge.isOffline() && !forceRefresh) {
			log('warn', 'ProductTree', 'Offline: No cached product trees available');
			return [];
		}

		const apiUrl = getApiUrl('Core/ProductTree');
		const response = await authenticatedFetch(apiUrl, {}, 'token');

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data = await response.json();

		await productTreeCache.put('all', data);

		log('info', 'ProductTree', `Fetched ${data?.length || 0} product trees`);
		return data;
	} catch (error) {
		log('error', 'ProductTree', `Error loading product trees: ${error.message}`);

		const cached = await productTreeCache.get('all');
		if (cached) {
			log('info', 'ProductTree', 'Returning cached product trees after error');
			return cached;
		}

		throw error;
	}
}

export async function getProductTreesTree(forceRefresh = false) {
	const flat = await getProductTrees(forceRefresh);
	return transformProductTreeData(flat);
}

export async function getProductTreeById(id) {
	try {
		const cached = await productTreeCache.get(id);
		if (cached) {
			return {
				id: cached.id,
				label: cached.label,
				title: cached.label,
				parentId: cached.parentId,
				depth: cached.depth,
				children: [],
			};
		}

		if (iframeDataBridge.isOffline()) {
			log('warn', 'ProductTree', `Offline: Product tree ${id} not found in cache`);
			return null;
		}

		const apiUrl = getApiUrl(`Core/ProductTree/${id}`);
		const response = await authenticatedFetch(apiUrl, {}, 'token');

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data = await response.json();

		await productTreeCache.put(id, data);

		return {
			id: data.id,
			label: data.label,
			title: data.label,
			parentId: data.parentId,
			depth: data.depth,
			children: [],
		};
	} catch (error) {
		log('error', 'ProductTree', `Error loading product tree ${id}: ${error.message}`);
		throw error;
	}
}

export async function clearProductTreeCache(id = null) {
	if (id) {
		await productTreeCache.delete(id);
	} else {
		await productTreeCache.clearAll();
	}
}

export async function cleanOldCacheEntries(maxAgeInDays = 1) {
	await productTreeCache.cleanOld(maxAgeInDays);
}

export async function getCacheStats() {
	return await productTreeCache.getStats();
}

cleanOldCacheEntries();
